import {
  getPurchaseFlowBookingById,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import {
  purchaseFlowBookingIdSelector,
  purchaseFlowBookingSelector,
} from '@/features/purchase/purchaseSelectors';
import { useExpiryCountdown } from '@/hooks/useExpiryCountdown';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type { CountdownTime } from '@/hooks/useExpiryCountdown';

export function useInProgressBooking() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingId = useSelector(purchaseFlowBookingIdSelector);
  const booking = useSelector(purchaseFlowBookingSelector);
  const [isValidating, setIsValidating] = useState(true);
  const countdown = useExpiryCountdown(booking?.confirmableUntil);

  // Fetch booking if only ID exists (persisted from localStorage)
  useEffect(() => {
    if (bookingId && !booking) {
      dispatch(getPurchaseFlowBookingById(bookingId));
    } else if (booking) {
      // Defensive check: if booking exists from rehydration, validate expiry BEFORE showing modals
      const isExpired = booking.confirmableUntil
        ? isAfter(new Date(), new Date(booking.confirmableUntil))
        : false;

      if (isExpired) {
        // Clean up immediately and keep validating until cleanup completes
        dispatch(resetPurchase());
        persistor.purge().then(() => {
          setIsValidating(false);
        });
      } else {
        setIsValidating(false);
      }
    } else {
      setIsValidating(false);
    }
  }, [bookingId, booking, dispatch]);

  // Auto-cleanup expired bookings
  useEffect(() => {
    if (!booking?.confirmableUntil) return;

    if (isAfter(new Date(), new Date(booking.confirmableUntil))) {
      dispatch(resetPurchase());
      persistor.purge();
    }
  }, [booking?.confirmableUntil, dispatch]);

  const handleCheckoutClick = useCallback(
    (onBeforeNavigate?: () => void) => {
      onBeforeNavigate?.();
      navigate('/purchase/checkout');
    },
    [navigate]
  );

  return {
    bookingId,
    booking,
    isValidating,
    countdown,
    handleCheckoutClick,
  };
}
